import { useRef, useState } from 'react'

import CloseSvg from './close-svg'

import FlexCol from '@/components/shared/FlexCol'
import FlexRow from '@/components/shared/FlexRow'
import { amplitudeTracker } from '@/utils/amplitude'

export const GlassisForm = ({ closeForm, onCloseForm }: { closeForm: () => void; onCloseForm?: () => void }) => {
  // 전화번호 각 필드 상태
  const [phone1, setPhone1] = useState('010')
  const [phone2, setPhone2] = useState('')
  const [phone3, setPhone3] = useState('')
  // 동의 체크박스 상태
  const [agreed, setAgreed] = useState(false)

  // input ref
  const phone1Ref = useRef<HTMLInputElement>(null)
  const phone2Ref = useRef<HTMLInputElement>(null)
  const phone3Ref = useRef<HTMLInputElement>(null)

  // 입력값이 모두 채워졌는지 확인
  const isPhoneValid = phone1.length === 3 && phone2.length === 4 && phone3.length === 4

  // 제출 가능 여부
  const canSubmit = isPhoneValid && agreed

  // 등록하기 클릭/엔터 핸들러
  const handleSubmit = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!canSubmit) return
    const phone = `${phone1}-${phone2}-${phone3}`
    amplitudeTracker.track('submit_glassis_teaser_form', {
      phone,
    })
    const identifyEvent = new amplitudeTracker.Identify()
    identifyEvent.set('phone', `+8210${phone2}${phone3}`)
    amplitudeTracker.identify(identifyEvent)
    onCloseForm?.()
    closeForm()
  }

  // 이벤트가 아래 레이어로 전파되지 않도록 최상위 레이어에서 캡처
  const handleFormLayerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // 닫기 버튼이 아닌 바깥쪽 클릭 시 닫기 등 추가 동작이 필요하면 여기에 구현
  }

  // 숫자만 입력되도록 처리 및 자동 포커스 이동
  const handleNumberInput =
    (setter: (v: string) => void, maxLength: number, nextRef?: React.RefObject<HTMLInputElement>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/[^0-9]/g, '')
      if (value.length > maxLength) value = value.slice(0, maxLength)
      setter(value)
      if (value.length === maxLength && nextRef && nextRef.current) {
        nextRef.current.focus()
      }
    }

  // 엔터/탭 시 다음 input으로 이동
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, nextRef?: React.RefObject<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      if (nextRef && nextRef.current) {
        nextRef.current.focus()
      }
    }
  }

  // 마지막 input에서 엔터 시 제출
  const handleLastInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (canSubmit) {
        closeForm()
      }
    }
  }

  return (
    <FlexCol
      className="w-full h-full relative bg-black px-5 items-center justify-center gap-5"
      onClick={handleFormLayerClick}
      onMouseDown={handleFormLayerClick}
      onTouchStart={(e) => {
        e.stopPropagation()
      }}
    >
      <FlexCol className="w-full max-w-[356px] px-[30px] pt-[60px] pb-5 bg-white inline-flex flex-col items-center">
        <form className="w-full" onSubmit={handleSubmit} autoComplete="off">
          <FlexCol className="gap-14">
            <FlexCol className="justify-start items-center gap-5">
              <FlexCol className="justify-center items-center gap-12">
                <FlexCol className="items-center gap-2.5">
                  <div className="h-6 text-center justify-start text-black text-base font-bold font-['Akzidenz-Grotesk_Next'] leading-tight tracking-wider">
                    SEE WHAT’S COMING
                  </div>
                  <div className="text-center justify-start text-black text-base font-medium font-['Noto_Sans_KR'] leading-normal tracking-wide">
                    7월 1일, 가장 먼저 런칭 알림을
                    <br />
                    받고 싶다면 지금 등록하세요.
                  </div>
                </FlexCol>
                <FlexCol className="gap-2.5 px-4">
                  <FlexRow className="items-center gap-1 h-11">
                    <input
                      ref={phone1Ref}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      placeholder="010"
                      value={phone1}
                      onChange={handleNumberInput(setPhone1, 3, phone2Ref)}
                      onKeyDown={(e) => handleKeyDown(e, phone2Ref)}
                      className="w-full h-full bg-[#F0F0F0] py-4 text-center placeholder-[#AAAAAA] focus:outline-none font-['Akzidenz-Grotesk_Next']"
                    />

                    <input
                      ref={phone2Ref}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      placeholder="0000"
                      value={phone2}
                      onChange={handleNumberInput(setPhone2, 4, phone3Ref)}
                      onKeyDown={(e) => handleKeyDown(e, phone3Ref)}
                      className="w-full h-full bg-[#F0F0F0] py-4 text-center placeholder-[#AAAAAA] focus:outline-none font-['Akzidenz-Grotesk_Next']"
                    />

                    <input
                      ref={phone3Ref}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      placeholder="0000"
                      value={phone3}
                      onChange={handleNumberInput(setPhone3, 4)}
                      onKeyDown={handleLastInputKeyDown}
                      className="w-full h-full bg-[#F0F0F0] py-4 text-center placeholder-[#AAAAAA] focus:outline-none font-['Akzidenz-Grotesk_Next']"
                    />
                  </FlexRow>
                  <button
                    className={`h-11 py-5 rounded-sm outline outline-1 outline-offset-[-1px] inline-flex justify-center items-center gap-2.5 ${
                      canSubmit ? 'bg-black cursor-pointer outline-zinc-300' : 'cursor-not-allowed outline-[#D7D7D7]'
                    }`}
                    disabled={!canSubmit}
                    type="submit"
                  >
                    <div
                      className={`text-center justify-start text-base font-medium font-['Noto_Sans_CJK_KR'] leading-tight tracking-wide ${
                        canSubmit ? 'text-white' : 'text-[#D7D7D7]'
                      }`}
                    >
                      등록하기
                    </div>
                  </button>
                </FlexCol>
              </FlexCol>
              <FlexRow className="justify-center items-center gap-2 mt-2">
                <input
                  id="agree"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 border border-black accent-black"
                />
                <label
                  htmlFor="agree"
                  className="text-center justify-start text-black text-sm font-normal font-['Noto_Sans_KR'] leading-tight tracking-wide cursor-pointer"
                >
                  동의합니다
                </label>
              </FlexRow>
            </FlexCol>
            <div className="text-neutral-400 font-['Noto_Sans_KR'] font-normal text-[10px] leading-[14px] tracking-normal text-[#AAAAAA]">
              개인정보 수집 및 이용 동의
              <br />
              (주)힐링페이퍼는 다음과 같이 귀하의 개인정보를 수집⋅이용합니다.
              <br />
              <ul className="list-disc ml-4 mt-1 mb-1">
                <li>수집⋅이용목적: 웹페이지 런칭 알림 전송</li>
                <li>수집⋅이용항목: 전화번호</li>
                <li>보유⋅이용기간: 동의일로부터 3개월간</li>
              </ul>
              (단, 관계법령에 따라 보관해야 하는 정보는 해당 법령에서 정하는
              <br />
              기간만큼 보관합니다.)
              <br />※ 동의하지 않으실 경우 알림 전송이 불가합니다.
            </div>
          </FlexCol>
        </form>
      </FlexCol>
      <button
        className="font-white text-white text-2xl"
        onClick={(e) => {
          e.stopPropagation()
          closeForm()
        }}
      >
        <CloseSvg />
      </button>
    </FlexCol>
  )
}
